import { useEffect } from 'react'
import { useLocation, useSearchParams } from 'react-router-dom'
import { useToast } from '@/hooks/use-toast'
import { useAuth } from '@/context/AuthContext'
import { MatchHistory } from '@/components/MatchHistory'
import { MatchdaySection } from '@/sections/matchday'
import { MyPicksSection } from '@/sections/my-picks'
import { CompetitionSection } from '@/sections/competition'
import { StandingsSection } from '@/sections/standings'
import { UpcomingFixturesSection } from '@/sections/upcoming-fixtures'
import { LeagueRulesSection } from '@/sections/league-rules'

// Map URL paths to section keys
const PATH_TO_SECTION: Record<string, string> = {
  '/dashboard': 'current-matchday',
  '/picks': 'picks',
  '/league': 'league',
  '/standings': 'standings',
  '/fixtures': 'fixtures',
  '/rules': 'rules',
  '/history': 'history',
}

export const ManagerDashboard = () => {
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const { toast } = useToast()
  const { currentMembership } = useAuth()

  // Key to force all sections to remount when the league changes.
  // This resets local state, refs, and React Query hooks so stale
  // data from the previous league is never shown.
  const leagueKey = currentMembership?.league_id ?? 'none'

  // Handle payment redirect query params (run once on mount)
  useEffect(() => {
    const payment = searchParams.get('payment')
    if (payment === 'success') {
      toast({ title: 'Payment successful', description: 'Your league entry fee has been paid.' })
      searchParams.delete('payment')
      setSearchParams(searchParams, { replace: true })
    } else if (payment === 'cancelled') {
      toast({ title: 'Payment cancelled', description: 'You can pay anytime from League Settings.', variant: 'destructive' })
      searchParams.delete('payment')
      setSearchParams(searchParams, { replace: true })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Determine active section from current path
  const activeSection = PATH_TO_SECTION[location.pathname] || 'current-matchday'

  return (
    <div key={leagueKey} className="px-4 py-4 sm:py-6">
      {activeSection === 'current-matchday' && (
        <MatchdaySection />
      )}

      {activeSection === 'picks' && (
        <MyPicksSection />
      )}

      {activeSection === 'league' && (
        <CompetitionSection />
      )}

      {activeSection === 'standings' && (
        <StandingsSection />
      )}

      {activeSection === 'fixtures' && (
        <UpcomingFixturesSection />
      )}

      {activeSection === 'rules' && (
        <LeagueRulesSection />
      )}

      {activeSection === 'history' && (
        <MatchHistory />
      )}
    </div>
  )
}
